/**
 * AdminVideoManager — Upload & manage immersion videos
 * Route: /abundance/admin/videos
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { ArrowLeft, Upload, Trash2, Film, GripVertical, Check, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PHASES = ['safety', 'opening', 'becoming', 'installation', 'integration'];
const API = process.env.REACT_APP_BACKEND_URL;

const AdminVideoManager = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Upload form state
  const [uploadPhase, setUploadPhase] = useState('safety');
  const [uploadDuration, setUploadDuration] = useState(16);
  const [uploadLabel, setUploadLabel] = useState('');

  const loadVideos = async () => {
    try {
      const res = await fetch(`${API}/api/videos/list`);
      if (res.ok) {
        const data = await res.json();
        setVideos(data.videos || []);
      }
    } catch (e) {
      console.error('Failed to load videos:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadVideos(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      setMessage({ text: 'Please select a video file (MP4)', type: 'error' });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setMessage({ text: '', type: '' });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('phase', uploadPhase);
    formData.append('duration', uploadDuration.toString());
    formData.append('order', videos.length.toString());
    formData.append('label', uploadLabel || file.name.replace(/\.[^/.]+$/, ''));

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/api/videos/upload`);

      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) {
          setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      };

      const result = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error(xhr.responseText || 'Upload failed'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });

      setMessage({ text: `Uploaded: ${result.label} (${result.size_mb}MB)`, type: 'success' });
      setUploadLabel('');
      loadVideos();
    } catch (err) {
      setMessage({ text: `Upload failed: ${err.message}`, type: 'error' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (fileId, label) => {
    if (!window.confirm(`Remove "${label}" from the immersion?`)) return;
    try {
      const res = await fetch(`${API}/api/videos/${fileId}`, { method: 'DELETE' });
      if (res.ok) {
        setMessage({ text: `Removed: ${label}`, type: 'success' });
        loadVideos();
      }
    } catch (e) {
      setMessage({ text: 'Failed to delete', type: 'error' });
    }
  };

  const handleUpdatePhase = async (fileId, newPhase) => {
    try {
      await fetch(`${API}/api/videos/${fileId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phase: newPhase }),
      });
      loadVideos();
    } catch (e) {
      console.error('Failed to update:', e);
    }
  };

  const handleUpdateDuration = async (fileId, newDuration) => {
    try {
      await fetch(`${API}/api/videos/${fileId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ duration: parseInt(newDuration) }),
      });
      loadVideos();
    } catch (e) {
      console.error('Failed to update:', e);
    }
  };

  // Drag-and-drop reorder — persist new order to backend
  const handleReorder = useCallback((newOrder) => {
    setVideos(newOrder);
  }, []);

  const persistOrder = useCallback(async () => {
    // Save the new order to backend
    const updates = videos.map((v, i) => ({ file_id: v.file_id, order: i }));
    try {
      await fetch(`${API}/api/videos/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: updates }),
      });
    } catch (e) {
      console.error('Failed to save order:', e);
    }
  }, [videos]);

  const phaseColor = (phase) => {
    const colors = {
      safety: 'rgba(100, 180, 255, 0.3)',
      opening: 'rgba(140, 200, 120, 0.3)',
      becoming: 'rgba(255, 200, 100, 0.3)',
      installation: 'rgba(255, 140, 100, 0.3)',
      integration: 'rgba(180, 140, 255, 0.3)',
    };
    return colors[phase] || 'rgba(255,255,255,0.1)';
  };

  return (
    <div className="min-h-screen bg-[#0a0c10] text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 px-6 py-5 flex items-center gap-4"
        style={{ background: 'linear-gradient(to bottom, rgba(10,12,16,0.95), rgba(10,12,16,0))', backdropFilter: 'blur(12px)' }}>
        <button onClick={() => navigate(-1)} className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 transition-all"
          style={{ border: '1px solid rgba(255,255,255,0.06)' }} data-testid="admin-videos-back-btn">
          <ArrowLeft size={18} className="text-white/50" />
        </button>
        <div>
          <h1 className="text-xl text-white/90" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
            Video Immersion Manager
          </h1>
          <p className="text-white/30 text-xs mt-0.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Upload and arrange your nature clips
          </p>
        </div>
      </header>

      <main className="px-6 pb-20 max-w-2xl mx-auto">
        {/* Upload Section */}
        <motion.div
          className="rounded-2xl p-6 mb-8"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="text-white/70 text-sm mb-4 tracking-wider uppercase" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Upload Video
          </h2>

          {/* Phase + Duration row */}
          <div className="flex gap-3 mb-4">
            <div className="flex-1">
              <label className="text-white/30 text-xs block mb-1.5" style={{ fontFamily: "'Manrope', sans-serif" }}>Phase</label>
              <select
                value={uploadPhase}
                onChange={(e) => setUploadPhase(e.target.value)}
                className="w-full bg-white/5 text-white/80 text-sm rounded-lg px-3 py-2.5 border border-white/10 outline-none focus:border-white/20"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid="upload-phase-select"
              >
                {PHASES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
              </select>
            </div>
            <div className="w-24">
              <label className="text-white/30 text-xs block mb-1.5" style={{ fontFamily: "'Manrope', sans-serif" }}>Duration (s)</label>
              <input
                type="number"
                value={uploadDuration}
                onChange={(e) => setUploadDuration(Number(e.target.value))}
                className="w-full bg-white/5 text-white/80 text-sm rounded-lg px-3 py-2.5 border border-white/10 outline-none focus:border-white/20"
                min={5} max={60}
                data-testid="upload-duration-input"
              />
            </div>
          </div>

          {/* Label */}
          <input
            type="text"
            value={uploadLabel}
            onChange={(e) => setUploadLabel(e.target.value)}
            placeholder="Label (e.g. 'Ocean sunrise')"
            className="w-full bg-white/5 text-white/80 text-sm rounded-lg px-3 py-2.5 border border-white/10 outline-none focus:border-white/20 mb-4"
            style={{ fontFamily: "'Manrope', sans-serif" }}
            data-testid="upload-label-input"
          />

          {/* Upload button */}
          <input ref={fileInputRef} type="file" accept="video/*" onChange={handleUpload} className="hidden" data-testid="upload-file-input" />

          <motion.button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm"
            style={{
              fontFamily: "'Manrope', sans-serif",
              background: uploading ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, rgba(90,120,80,0.8), rgba(65,95,55,0.9))',
              border: '1px solid rgba(140,180,120,0.2)',
            }}
            whileHover={!uploading ? { scale: 1.01 } : {}}
            whileTap={!uploading ? { scale: 0.98 } : {}}
            data-testid="upload-video-btn"
          >
            {uploading ? (
              <>
                <Loader2 size={16} className="text-white/60 animate-spin" />
                <span className="text-white/60">Uploading... {uploadProgress}%</span>
              </>
            ) : (
              <>
                <Upload size={16} className="text-white/80" />
                <span className="text-white/80">Choose Video File</span>
              </>
            )}
          </motion.button>

          {/* Upload progress bar */}
          {uploading && (
            <div className="mt-3 h-1 bg-white/5 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ background: 'rgba(90,120,80,0.8)' }}
                initial={{ width: 0 }}
                animate={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}
        </motion.div>

        {/* Message */}
        <AnimatePresence>
          {message.text && (
            <motion.div
              className={`mb-4 px-4 py-3 rounded-xl text-sm ${message.type === 'error' ? 'bg-red-500/10 text-red-300/80' : 'bg-green-500/10 text-green-300/80'}`}
              style={{ border: `1px solid ${message.type === 'error' ? 'rgba(255,100,100,0.15)' : 'rgba(100,255,100,0.15)'}` }}
              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              data-testid="upload-message"
            >
              {message.text}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Video List */}
        <h2 className="text-white/70 text-sm mb-4 tracking-wider uppercase" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Immersion Clips ({videos.length})
        </h2>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 size={24} className="text-white/30 animate-spin" />
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-12">
            <Film size={32} className="text-white/15 mx-auto mb-3" />
            <p className="text-white/30 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
              No videos uploaded yet
            </p>
          </div>
        ) : (
          <Reorder.Group
            axis="y"
            values={videos}
            onReorder={handleReorder}
            className="flex flex-col gap-3"
            data-testid="video-reorder-list"
          >
            {videos.map((video, index) => (
              <Reorder.Item
                key={video.file_id}
                value={video}
                onDragEnd={persistOrder}
                className="flex items-center gap-4 rounded-xl px-4 py-3 cursor-grab active:cursor-grabbing"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                whileDrag={{ scale: 1.02, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', background: 'rgba(255,255,255,0.06)' }}
                data-testid={`video-item-${index}`}
              >
                <GripVertical size={14} className="text-white/25 flex-shrink-0 cursor-grab" />

                {/* Order number */}
                <span className="text-white/25 text-xs w-5 text-center" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {index + 1}
                </span>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-white/75 text-sm truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {video.label}
                  </p>
                  <p className="text-white/25 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {video.duration}s · {(video.size / (1024 * 1024)).toFixed(1)}MB
                  </p>
                </div>

                {/* Phase selector */}
                <select
                  value={video.phase}
                  onChange={(e) => handleUpdatePhase(video.file_id, e.target.value)}
                  className="text-xs rounded-lg px-2 py-1.5 border outline-none"
                  style={{
                    background: phaseColor(video.phase),
                    borderColor: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.7)',
                    fontFamily: "'Manrope', sans-serif",
                  }}
                  data-testid={`video-phase-${index}`}
                >
                  {PHASES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                </select>

                {/* Duration input */}
                <input
                  type="number"
                  value={video.duration}
                  onChange={(e) => handleUpdateDuration(video.file_id, e.target.value)}
                  className="w-14 text-xs text-center rounded-lg px-1 py-1.5 bg-white/5 border border-white/10 text-white/60 outline-none"
                  min={5} max={60}
                  data-testid={`video-duration-${index}`}
                />

                {/* Delete */}
                <motion.button
                  onClick={() => handleDelete(video.file_id, video.label)}
                  className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 transition-colors"
                  whileTap={{ scale: 0.9 }}
                  data-testid={`video-delete-${index}`}
                >
                  <Trash2 size={14} className="text-white/30 hover:text-red-300" />
                </motion.button>
              </Reorder.Item>
            ))}
          </Reorder.Group>
        )}

        {/* Instructions */}
        <div className="mt-8 rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
          <h3 className="text-white/50 text-xs uppercase tracking-wider mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>How it works</h3>
          <ul className="text-white/30 text-xs space-y-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <li>Upload your MP4 nature clips (Artgrid, etc.)</li>
            <li>Drag and drop clips to reorder the sequence</li>
            <li>Assign each clip to an emotional arc phase</li>
            <li>Set the duration each clip plays before crossfading</li>
            <li>Videos play in order during the Video Immersion experience</li>
            <li>Works on all browsers including Safari</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default AdminVideoManager;
